/**
 * Compras — Importar nota fiscal. Rota `/app/compras/notas-fiscais/importar`.
 *
 * Sobe o XML do fornecedor e leva para a REVISÃO. Esta tela não grava nada —
 * quem grava é `TelaRevisarNotaFiscal`, depois de a pessoa conferir.
 *
 * Fluxo (migração 0011 + spec da tarefa):
 *
 * 1. A pessoa escolhe o arquivo `.xml`. A tela lê o TEXTO do arquivo no
 *    navegador (`File.text()`) e manda cru para `POST /notas-fiscais/analisar`
 *    — sem multipart, porque o arquivo é o corpo inteiro da requisição (ver
 *    `plataforma/notasfiscais.ts`).
 * 2. A resposta é a PRÉVIA: emitente, itens, totais e a sugestão de SKU para
 *    cada item cujo EAN bate com o catálogo. Nada disso está no banco ainda.
 * 3. A tela navega para a revisão levando a prévia E o XML — o arquivo volta
 *    ao servidor na conclusão, junto das decisões tomadas lá.
 * 4. Nota já importada NÃO é erro aqui: a prévia diz `ja_importada`, a revisão
 *    mostra a faixa e bloqueia a conclusão. Recusar no upload esconderia a
 *    nota que a pessoa quer conferir.
 *
 * Sem a barra de abas de Compras aqui de propósito: é um fluxo focado (subir
 * um arquivo), e "Voltar" já deixa claro de onde a pessoa veio.
 */
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { classesDeBotao } from '../../componentes/botao-estilo'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { analisarNotaFiscal } from '../../plataforma/notasfiscais'
import { LayoutApp } from '../app/LayoutApp'
import type { EstadoDaRevisao } from './TelaRevisarNotaFiscal'

const CAMINHO_REVISAR = '/app/compras/notas-fiscais/importar/revisar'

type Estado =
  | { fase: 'ocioso' }
  | { fase: 'lendo'; nomeArquivo: string }
  | { fase: 'erro'; mensagem: string }

export function TelaImportarNotaFiscal() {
  const navegar = useNavigate()
  const [estado, setEstado] = useState<Estado>({ fase: 'ocioso' })
  const referenciaInput = useRef<HTMLInputElement>(null)

  async function selecionarArquivo(arquivo: File | undefined) {
    if (!arquivo) return

    setEstado({ fase: 'lendo', nomeArquivo: arquivo.name })
    try {
      const xml = await arquivo.text()
      const previa = await analisarNotaFiscal(xml)

      const estadoDaRevisao: EstadoDaRevisao = { previa, xml, nomeArquivo: arquivo.name }
      navegar(CAMINHO_REVISAR, { state: estadoDaRevisao })
    } catch (causa: unknown) {
      setEstado({
        fase: 'erro',
        mensagem:
          causa instanceof ErroApi ? causa.message : 'Não foi possível ler o arquivo. Ele é um XML de NF-e?',
      })
    } finally {
      // Libera o input para o mesmo arquivo poder ser escolhido de novo (o
      // navegador não dispara `change` numa segunda seleção idêntica sem isso).
      if (referenciaInput.current) referenciaInput.current.value = ''
    }
  }

  const lendo = estado.fase === 'lendo'

  return (
    <LayoutApp
      trilha={[
        { rotulo: 'Compras', para: '/app/compras/notas-fiscais' },
        { rotulo: 'Notas fiscais', para: '/app/compras/notas-fiscais' },
        { rotulo: 'Importar' },
      ]}
      voltarPara="/app/compras/notas-fiscais"
      titulo="Importar nota fiscal"
      subtitulo="Suba o XML da NF-e de compra para conferir antes de gravar"
    >
      <div className="flex w-full flex-col gap-4">
        <div className="rounded-sistema border border-dashed border-borda bg-superficie px-6 py-12">
          <div className="mx-auto flex max-w-[440px] flex-col items-center gap-3 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-sistema bg-marca-suave">
              <Icone nome="documento_importar" tamanho={24} className="text-marca" />
            </span>

            <div>
              <p className="text-[14.5px] font-semibold text-tinta">Selecione o XML da NF-e de compra</p>
              <p className="mt-1 text-[12.5px] leading-[1.55] text-texto-suave">
                A loja lê o fornecedor, os itens e os impostos do arquivo e mostra tudo para você conferir.
                <span className="font-semibold text-texto"> Nada é gravado nesta etapa</span> — o estoque só
                se move quando você concluir a revisão.
              </p>
            </div>

            {/* `<label>` e não `<button>`: é o clique no rótulo que abre o
                seletor de arquivo do sistema operacional. Um botão aqui não
                abriria nada. As classes vêm de `classesDeBotao` para o visual
                não divergir do resto — é exatamente para este caso que ela é
                exportada. */}
            <label className={`mt-1 cursor-pointer ${classesDeBotao()}`}>
              <Icone nome="add_box" tamanho={18} />
              {lendo ? 'Lendo o arquivo…' : 'Escolher arquivo .xml'}
              <input
                ref={referenciaInput}
                type="file"
                accept=".xml,text/xml,application/xml"
                className="sr-only"
                disabled={lendo}
                onChange={(evento) => void selecionarArquivo(evento.target.files?.[0])}
              />
            </label>

            {lendo && <p className="tabular text-[12px] text-texto-suave">{estado.nomeArquivo}</p>}

            {estado.fase === 'erro' && (
              <div
                role="alert"
                className="mt-1 flex w-full items-start gap-2 rounded-sistema bg-erro-suave px-4 py-3 text-left text-[13px] text-erro-texto"
              >
                <Icone nome="error" tamanho={18} className="mt-px shrink-0" />
                <span>{estado.mensagem}</span>
              </div>
            )}
          </div>
        </div>

        {/* O que vem depois, dito antes: as três etapas são o contrato desta
            tela com quem está subindo um documento fiscal pela primeira vez. */}
        <div className="rounded-sistema border border-borda bg-superficie p-5">
          <h2 className="text-[13.5px] font-bold text-tinta">Como funciona</h2>
          <ol className="mt-3 flex list-none flex-col gap-3 p-0">
            {[
              {
                titulo: 'Você envia o XML',
                texto: 'O mesmo arquivo que o fornecedor manda por e-mail. Ele não é gravado ainda.',
              },
              {
                titulo: 'A loja concilia o que dá',
                texto:
                  'Item cujo código de barras já existe no seu catálogo é ligado sozinho ao SKU. O que sobrar, você resolve na revisão — a loja nunca adivinha SKU por descrição.',
              },
              {
                titulo: 'Você decide e conclui',
                texto:
                  'Na revisão você escolhe se o estoque entra agora. Só então a nota, o fornecedor e o saldo são gravados.',
              },
            ].map((passo, indice) => (
              <li key={passo.titulo} className="flex items-start gap-3">
                <span className="tabular flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-divisoria text-[12px] font-bold text-texto">
                  {indice + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold text-tinta">{passo.titulo}</p>
                  <p className="mt-0.5 text-[12.5px] leading-[1.55] text-texto-suave">{passo.texto}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </LayoutApp>
  )
}
